import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { FunctionUtil } from '@shared/utils';
import moment from 'moment';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { MatDatepicker } from '@angular/material/datepicker';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Moment } from 'moment';

export const DATE_INPUT_FORMATE = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AppDateInputComponent,
      multi: true,
    },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_INPUT_FORMATE },
  ],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'date-field',
  },
})
export class AppDateInputComponent
  extends BaseComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('calendar') calendar: ElementRef;

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (
      this.isOpen &&
      !this.eRef.nativeElement.contains(event.target) &&
      !this.calendar.nativeElement?.contains(event.target) &&
      !this.isInsideForeignOverlay(event.target) &&
      this.closeWhenClickOut &&
      !this.isYearSelected
    ) {
      this.close();
      this.isYearSelected = false;
    }
  }

  @Input() title: string;
  @Input() required = false;
  @Input('min') minDate: Date | null = null;
  @Input('max') maxDate: Date | null = null;
  @Input() disabled = false;
  @Input() enableEditing = false;
  @Input() enableTime = false;
  @Input() selectMonthAndYear = false;
  @Input() selectYear = false;
  @Input() startView = 'month';
  @Input() closeWhenClickOut = true;
  @Input() placeHolder = 'Placeholder.DatePlaceholder';
  @Output() changeEvent = new EventEmitter<any>();
  value: Date;
  /**
   * Beside the input first, so a tall panel does not run off the bottom and
   * hide the actions. start/end are direction-aware, so this flips in RTL.
   */
  readonly calendarPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  isOpen = false;
  isYearSelected = false

  /** Set from the form control, alongside the `disabled` input. */
  private controlDisabled = false;

  get isDisabled(): boolean {
    return this.disabled || this.controlDisabled;
  }

  onTouched: any = () => {};
  onChange: any = () => {};

  form: FormGroup = this.formBuilder.group({
    date: [],
    time: [],
  });

  constructor(private formBuilder: FormBuilder, private eRef: ElementRef) {
    super();
  }

  ngOnInit(): void {
    this.form
      .get('time')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.form.get('date').value) {
          this.previewValue();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled?.currentValue) {
      this.form.disable();
    } else if (changes.disabled) {
      this.form.enable();
    }

    if (changes.required?.currentValue === true) {
      this.form.get('date').setValidators([Validators.required]);
      if (this.enableTime) {
        this.form.get('time').setValidators([Validators.required]);
      }
    }
  }

  dateSelected(event: any) {
    if (this.enableEditing && event.value) {
      this.form.get('date').setValue(FunctionUtil.convertDate(new Date(event.value)));
    } else {
      this.form.get('date').setValue(event.value);
    }
    this.changeEvent.emit(FunctionUtil.convertDate(new Date(event.value)));
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  writeValue(date: any): void {
    if (date) {
      this.form.setValue({ date, time: moment(date).format('HH:mm') });
      this.value = date;
    } else {
      this.form.setValue({ date: null, time: null });
      this.value = null;
    }
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    // The field is disabled by either source, so a disabled form control greys it out and
    // stops it opening — without this the calendar still opened on a disabled control.
    // Kept separate from the `disabled` input, which Angular would otherwise overwrite here
    // on setup for a control that binds both.
    this.controlDisabled = disabled;

    if (disabled) {
      this.isOpen = false;
      this.form.disable({ emitEvent: false });
    } else if (!this.disabled) {
      this.form.enable({ emitEvent: false });
    }
  }

  /**
   * The time field's `mat-select` panels open in their own overlay attached to the body, so
   * picking an hour/minute — or clicking away to dismiss the list, which lands on the select's
   * own transparent backdrop — reads as a click outside and used to close the whole popup.
   * This popup's own backdrop is opaque, so clicking away from it still closes it as before.
   */
  private isInsideForeignOverlay(target: EventTarget | null): boolean {
    const element = target as HTMLElement;
    if (element?.classList?.contains('cdk-overlay-transparent-backdrop')) {
      return true;
    }
    const pane = element?.closest?.('.cdk-overlay-pane');
    return !!pane && !pane.contains(this.calendar?.nativeElement);
  }

  onKeydown(event): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  close(): void {
    // this.reset();
    this.isOpen = false;
    this.onTouched();
  }

  open(): void {
    if (this.isDisabled) {
      return;
    }
    this.isOpen = true;
  }

  updateValue(): void {
    this.value = this.parseFormValue();
    this.onChange(this.value);
    this.onTouched();
    this.isOpen = false;
  }

  previewValue(): void {
    this.value = this.parseFormValue();
  }

  parseFormValue(): Date {
    const { date, time } = this.form.value;
    const [hours, minutes] = time ? time.split(':') : [0, 0];
    const dateTime = moment(date);
    dateTime.hours(+hours);
    dateTime.minutes(+minutes);
    return dateTime.toDate();
  }

  yearSelected(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>) {
    this.isYearSelected = true;
    if (this.selectYear) {
      const date = normalizedMonthAndYear.toDate();

      this.form.setValue({
        date,
        time: null,
      });

      this.updateValue();
    }
  }


  setMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>) {
    const date = normalizedMonthAndYear.toDate();

    this.form.setValue({
      date,
      time: null,
    });

    this.updateValue();
  }
}
